// SDK singleton client — split out of sdk.js so every OTHER export in that
// file (getMe/login/sdkLogout/isAuthenticated/getUserProfileCached/
// listOrganizationsCached/fetchPublicProjectFromSDK/
// fetchPublicProjectVisibilityFromSDK/unlockPublicProjectFromSDK/
// fetchProjectFromSDK/resolveEnvPublishConfig/…) can `import { getSDK } from
// './sdkClient.js'` instead of bare-calling a same-file sibling export.
//
// FA204 / frank trap (project_frank_drops_module_local_helpers): a
// same-file sibling-export/module-local read is unresolvable once the
// reading function is individually frank.toJSON'd for platform delivery; a
// cross-file ESM import resolves fine via frank's globalScope prototype-
// chain fallback. `getSDK` used to be bare-called from a DOZEN sibling
// exports in sdk.js — the usual per-export inline-copy recipe would mean
// duplicating this whole stateful singleton (SDK construction, the
// init-await Proxy, the deferred-subscribe shim) a dozen times, each an
// INDEPENDENT copy that would silently diverge from real singleton
// semantics (a fresh-per-call `_sdkByKey` Map is not a cache at all).
// Moving it here once, imported everywhere it's read, gets the same
// frank-safety with a single, genuinely-shared implementation.
//
// `_sdkByKey`/`_initPromiseByKey`/`_crossAppHydration` are re-homed onto
// `globalThis.__sdkClientState` (the aiService.js/pointerCapability.js
// template) rather than plain module `let`/`Map()` — this file's OWN
// `getSDK`/`awaitSDKInit` still reference each other/module-locals bare
// (a same-file trap, just a much smaller one now), so each of THIS file's
// three exports below carries its own self-contained inline copy of
// whichever locals it needs, same recipe as every other file in this sweep.

import { SDK } from '@symbo.ls/sdk'
import { apiUrl as channelApiUrl } from '@symbo.ls/channels'
import { clearAuthCookie, hydrateAuthFromIframeBridge } from '../crossAppAuth.js'
import { reacquireBridgeSession } from './sessionBridgeReacquire.js'
import { isSessionBridgeBorrower } from './sessionBridgePeerOrigin.js'
import { installBridgeSessionRenewal } from './sessionBridgeRenewal.js'
import { workspaceApiBase } from './workspaceApiBase.js'
import { getActiveWorkspacePointer } from './activeWorkspacePointer.js'

export function getSDK({ useLocal = false, useNext = false } = {}) {
  // FA204 / frank trap — self-contained inline copy of the shared-state
  // accessor; every one of this file's exports carries its own.
  function sdkClientState() {
    return (globalThis.__sdkClientState = globalThis.__sdkClientState || {
      sdkByKey: new Map(),
      initPromiseByKey: new Map(),
      // Resolves once the cross-app iframe handoff has settled (or
      // immediately when there is nothing to do). Boot awaits this BEFORE
      // deciding a user is signed out, so a dev canvas does not redirect to
      // /login while the peer's session is still in flight. See
      // `awaitCrossAppHydration` below.
      crossAppHydration: null
    })
  }

  // SDK token provider for /workspace/* surfaces.
  //
  // The wrapper at <channel>.api.symbols.app/workspace (and the local
  // express bridge) authenticates by reading `Authorization: Bearer <jwt>`.
  // It resolves scope from the SDK Mongo token (`symbols_access_token`):
  // `workspace-project/src/auth.js claimsToScope` calls the injected
  // userResolver, which reads `User.activeOrganization` from Mongo LIVE and
  // derives the home workspace — so an org-switch or workspace-rename takes
  // effect immediately. (The old governance Supabase-JWT fallback was
  // removed with that project.)
  async function workspaceTokenProvider() {
    if (typeof window === 'undefined') return null
    // PERSONA (per-tab) — the sessionStorage overlay wins over the admin's
    // own localStorage token. This provider is wired into EVERY
    // /workspace-project/* request (chat, calendar, documents, tickets) and
    // read localStorage directly, bypassing TokenManager entirely; while a
    // persona was active that meant those surfaces kept answering with FULL
    // admin data while the rest of the app was narrowed — one app, two
    // identities, no error anywhere. The key is TokenManager's
    // `personaStorageKey` (`symbols_` + `persona_access_token`), which is
    // sessionStorage and therefore per-tab by construction; the literal is
    // inlined per the FA204 discipline this file already follows.
    try {
      const personaToken = window.sessionStorage.getItem('symbols_persona_access_token')
      if (personaToken) return personaToken
    } catch {
      /* locked storage — fall through to the base session token */
    }
    try {
      return window.localStorage.getItem('symbols_access_token')
    } catch {
      return null
    }
  }

  // Synchronous read of the active-workspace id used to stamp telemetry
  // envelopes. Matches the canonical client-side source bootShell already
  // reads (`localStorage.activeWorkspace`). Returns null when the value
  // is unavailable (first-time visitor, SSR, locked storage) — the server
  // then falls through to its scope resolver for authenticated requests.
  function readActiveWorkspaceId() {
    if (typeof window === 'undefined') return null
    try {
      return getActiveWorkspacePointer() || null
    } catch {
      return null
    }
  }

  // ── SDK instances per apiUrl ────────────────────────────────────────────
  // The SDK (see sdk/src/config/environment.js) picks its own apiUrl based
  // on `SYMBOLS_APP_ENVIRONMENT`; we only override with an explicit
  // `apiUrl` when the caller opts into `--local` or `--next` so we don't
  // re-hardcode the production URL here. Channel URLs come from
  // @symbo.ls/channels — single source of truth across sdk, smbls, server,
  // editor, workspace, platform.
  function resolveApiUrl({ useLocal, useNext } = {}) {
    if (useLocal) return channelApiUrl('local')
    if (useNext) return channelApiUrl('next')
    // Single source of truth — `workspaceApiBase` resolves through
    // `@symbo.ls/channels` after honoring runtime + localStorage
    // overrides + hostname autodetect (project-preview wildcards
    // like `<owner>--<key>.dev.preview.symbols.app` resolve to the
    // matching tier's API). The SDK and every other workspace API
    // consumer (agentMessages, dashboard slug check) share this base
    // so flipping one knob
    // (`localStorage.symbols_api_channel='localNext'`, or
    // `localStorage.symbols_workspace_api='https://...'`) routes every
    // /workspace/* and /core/* call at once.
    if (typeof window !== 'undefined') return workspaceApiBase()
    return undefined
  }

  // These methods read local state synchronously and must not be wrapped —
  // callers use them as guards (!!sdk.getAuthToken()) before async work.
  const SYNC_PASS = new Set(['getAuthToken', 'isReady', 'getToken', 'logout'])

  // `subscribeUserEvents` is auto-proxied onto the SDK from AuthService
  // AFTER `sdk.initialize()` resolves — it's NOT on the SDK at construction
  // time. The shell calls `el.sdk.subscribeUserEvents({...})` from
  // onCreate, which runs before init can complete, so a naive sync-pass
  // returns `undefined` and throws TypeError. We can't make it
  // Promise<fn> either — callers need a sync unsubscribe handle to bind
  // into `el.onRemove`. Solution: return a shim that schedules the real
  // subscribe-after-init and returns a sync cancel token that no-ops if
  // the deferred subscribe is still pending.
  function deferredSubscribeUserEvents(sdk, initPromise) {
    return (handlers) => {
      let realUnsub = null
      let cancelled = false
      initPromise
        .then(() => {
          if (cancelled) return
          const fn = sdk.subscribeUserEvents
          // Capability check, not error. Older SDK versions (pre-
          // AuthService proxy expansion) don't expose subscribeUserEvents
          // — the workspace shell tolerates that gracefully (no realtime,
          // but no spam either). Was a `console.warn` that flooded every
          // mount on version-skewed deploys; the call site is purely
          // additive, not load-bearing.
          if (typeof fn !== 'function') return
          try {
            realUnsub = fn.call(sdk, handlers)
          } catch (err) {
            console.warn('[sdk] subscribeUserEvents threw:', err?.message || err)
          }
        })
        .catch(() => {})
      return () => {
        cancelled = true
        if (typeof realUnsub === 'function') {
          try {
            realUnsub()
          } catch (_) {}
        }
      }
    }
  }

  // THE FOUR SYNCHRONOUS READS BEFORE INIT. The boot exposes this proxy as
  // el.sdk before `initialize()` settles (shared bootPrepare, both builds),
  // and the SDK builds its services IN initialize() — so before it
  // `getAuthToken()` threw "Service 'auth' not found". The answer must not
  // depend on timing: until the init settles the token reads come from the
  // stored session (what the TokenManager loads on init), isReady() is
  // false, and logout() waits for the init it would otherwise race
  // (sdkClient.lateInit.test.js).
  function syncBeforeInit(target, prop, value, initPromise) {
    // Only BEFORE init settles (tracked from the wrap, see wrapWithInitAwait):
    // after it, an error is a real error and is rethrown — never hidden
    // behind the stored token (one-boot review D10).
    return (...args) => {
      try {
        return value.apply(target, args)
      } catch (err) {
        if (!initPromise || initPromise.__settled) throw err
        if (prop === 'isReady') return false
        if (prop === 'logout') return initPromise.then(() => value.apply(target, args))
        // exactly the key the SDK's TokenManager loads on init — the same
        // identity it will answer with, and never a token that is not there
        try {
          return globalThis.localStorage?.getItem?.('symbols_access_token') || null
        } catch (_) {
          return null
        }
      }
    }
  }

  // Wrap the SDK so every method call awaits initialization first.
  // Property reads for non-function values (e.g. sdk.environment,
  // sdk.rootBus) pass through synchronously.
  function wrapWithInitAwait(sdk, initPromise) {
    // settle tracking starts with the wrap, so the first read after init
    // already sees it settled
    if (initPromise && !initPromise.__settleTracked) {
      initPromise.__settleTracked = true
      initPromise.__settled = false
      initPromise.then(
        () => (initPromise.__settled = true),
        () => (initPromise.__settled = true)
      )
    }
    return new Proxy(sdk, {
      get(target, prop) {
        if (prop === 'subscribeUserEvents') {
          return deferredSubscribeUserEvents(target, initPromise)
        }
        const value = target[prop]
        if (typeof value !== 'function') return value
        if (SYNC_PASS.has(prop)) return syncBeforeInit(target, prop, value, initPromise)
        return (...args) => initPromise.then(() => value.apply(target, args))
      }
    })
  }

  // Default: derive apiUrl from the active channel via workspaceApiBase()
  // (which honors hostname autodetect + localStorage overrides + build-
  // time NODE_ENV). `useNext: true` is opt-in only — it forces the `next`
  // channel regardless of host, kept as an escape hatch for explicit
  // testing against next.api.
  // THE NULL CLIENT — for a host where the SDK class never arrived.
  //
  // `SDK` comes from a top-level `import { SDK } from '@symbo.ls/sdk'`, and
  // frank stubs every cross-package specifier (`stubExternalsPlugin`,
  // smbls/plugins/frank/toJSON.js). On a served record the read collapses, so
  // `new SDK(opts)` threw `__scope.__import_sdk2.SDK is not a constructor` as
  // an UNHANDLED REJECTION — measured on the served UI-kit catalog
  // (2026-09-20) from TicketsDetailModal and PromptGalleryChip, neither of
  // which needs a live backend to render. A rejection there takes the whole
  // surface down; a client that answers "nothing" does not.
  //
  // WHAT IT PROMISES, stated so a caller can rely on it:
  //   • `isNullClient === true` — the hard signal. A caller that must NOT
  //     pretend it reached a backend reads this, not a method result.
  //   • the four SYNC_PASS guards stay SYNCHRONOUS (`!!sdk.getAuthToken()` is
  //     used as a signed-in test; a promise there reads truthy and sends a
  //     signed-out user down the signed-in path).
  //   • `rootBus.on` answers a no-op unsubscribe — six shared components
  //     dereference it directly.
  //   • `subscribeUserEvents` answers a SYNCHRONOUS unsubscribe, the same
  //     contract `deferredSubscribeUserEvents` above keeps, because the shell
  //     binds the return straight into `el.onRemove`.
  //   • every other method resolves `null` — the unwrapped shape the real
  //     `execute` resolves (BaseService._call already unwraps `{success,data}`
  //     → `data`), so `commentRowsFrom(null)` and every `?.` guard behave.
  //   • `then`/`catch`/`finally` and every Symbol answer undefined, so the
  //     client is never mistaken for a thenable by an `await`.
  // It warns ONCE per host. A per-call warning floods the console of a host
  // that simply has no backend.
  //
  // FA204 / frank trap — inlined here rather than hoisted to module scope,
  // the same discipline every other export in this file follows.
  function nullSDKClient(state) {
    if (!state.nullClient) {
      console.warn(
        '[sdk] @symbo.ls/sdk resolved without a constructor in this host — ' +
          'serving a null client. Every call answers null and no backend is reached.'
      )
      const noop = () => {}
      const bus = { on: () => noop, off: noop, emit: noop, once: () => noop }
      const sync = {
        getAuthToken: () => null,
        getToken: () => null,
        isReady: () => false,
        logout: noop
      }
      state.nullClient = new Proxy(
        {},
        {
          get(_target, prop) {
            if (typeof prop !== 'string') return undefined
            if (prop === 'then' || prop === 'catch' || prop === 'finally') return undefined
            if (prop === 'isNullClient') return true
            if (prop === 'rootBus') return bus
            if (prop === 'environment') return null
            if (prop === 'subscribeUserEvents') return () => noop
            if (prop in sync) return sync[prop]
            return () => Promise.resolve(null)
          }
        }
      )
    }
    return state.nullClient
  }

  const apiUrl = resolveApiUrl({ useLocal, useNext })
  const cacheKey = apiUrl || 'default'
  const state = sdkClientState()
  if (typeof SDK !== 'function') return nullSDKClient(state)
  // FAIL CLOSED: in a browser, '' means workspaceApiBase found no host rule
  // and no injected deployment identity. An SDK built without apiUrl falls to
  // its registry default (production) — the wrong plane. Serve the null
  // client instead (workspaceApiBase.failClosed.test.js).
  if (typeof window !== 'undefined' && apiUrl === '') return nullSDKClient(state)
  if (!state.sdkByKey.has(cacheKey)) {
    // HOTFIX 2026-10-08 (P0): the `smbls_session` cookie on Domain=symbols.app
    // carried the access + refresh token to EVERY *.symbols.app page — user
    // project code on `<owner>--<key>.preview.symbols.app` included — and dev +
    // prod shared it. Never read it (was hydrateAuthFromCookie), never write it
    // (was installAuthSync); delete the copy a previous build left. Cross-host
    // sign-in goes through the iframe bridge below
    // (__tests__/sdkClientNoSessionCookie.test.js).
    try {
      if (typeof clearAuthCookie === 'function') clearAuthCookie()
    } catch (_) {
      /* no document (SSR) — nothing to delete */
    }
    // The cookie strategy above only works on prod `.symbols.app` — Chrome
    // silently drops cookies with `domain=.localhost`, which is why local
    // dev needs the iframe handoff instead. `hydrateAuthFromIframeBridge`
    // was written and exported for exactly that, but had NO callers, so a
    // dev canvas never picked up the workspace session and bounced to
    // /login. bootShell's own comment ("cookie/iframe handoff lands
    // asynchronously") already assumed this ran.
    //
    // Self-guarding: resolves false immediately when the local session is
    // already fresh, when there is no peer origin, or when the peer
    // resolves to this same origin — so it is a no-op in prod and on a
    // signed-in tab.
    //
    // ACCESS-ONLY (2026-10-09): the bridge hands over the access token only
    // (sessionBridgeTokens.js). `reacquireBridgeSession({ ifStale: true })`
    // also never borrows on an origin that owns its own refresh token, and
    // drops a copied refresh token an older build left here. The SDK's
    // `hydrateAuthFromIframeBridge` stays as the fallback for a realm without
    // the new helper.
    //
    // OWNER vs PEER vs COPY (review M2 + D1, 2026-10-09; Nika: "No, avoid the
    // sign-out"). symbols_session_source: 'own' = a sign-in on THIS origin wrote
    // the refresh token (markOwnSession / the SDK); 'bridge' = adopted from the
    // /_session-bridge peer. An origin with an own refresh token owns the login:
    // no reacquirer — when the server refuses it, it signs out, never borrows.
    // A refresh token WITHOUT the 'own' mark:
    //   - on the shell (not a borrower): its own login from before the mark
    //     existed → mark it 'own';
    //   - on the canvas (the borrower): a COPY an older bridge build handed
    //     over. Take it out of storage BEFORE the SDK loads (so the SDK can
    //     never rotate the shell's token), ask the bridge, and adopt its token.
    //     If the bridge has no session, the token was this origin's own login:
    //     put it back, mark it 'own'. Never a sign-out either way.
    let ownsRefreshToken = false
    let legacyRefreshCopy = null
    let restoredOwnRefresh = false
    // D1 (live on canvas.dev, 2026-10-11): restore a copy as 'own' ONLY on
    // the peer's explicit ok:false, and never on a 'bridge'-marked origin.
    let copyWasBridgeMarked = false
    let peerSaidNoSession = false
    try {
      if (typeof window !== 'undefined') {
        const ls = window.localStorage
        const refresh = ls.getItem('symbols_refresh_token')
        if (refresh && ls.getItem('symbols_session_source') !== 'own') {
          if (
            typeof isSessionBridgeBorrower === 'function' &&
            isSessionBridgeBorrower() &&
            typeof reacquireBridgeSession === 'function'
          ) {
            legacyRefreshCopy = refresh
            copyWasBridgeMarked = ls.getItem('symbols_session_source') === 'bridge'
            ls.removeItem('symbols_refresh_token')
          } else {
            ls.setItem('symbols_session_source', 'own')
          }
        }
        ownsRefreshToken = !!ls.getItem('symbols_refresh_token')
      }
    } catch (_) {
      /* locked storage — treat as no own login */
    }
    // The SDK (>= 3.14.813) passes its re-ask options ({ timeoutMs, onNoSession }).
    const bridgeReacquirer =
      !ownsRefreshToken && typeof reacquireBridgeSession === 'function'
        ? (opts) => reacquireBridgeSession(opts)
        : undefined
    const restoreOwnRefresh = () => {
      try {
        window.localStorage.setItem('symbols_refresh_token', legacyRefreshCopy)
        window.localStorage.setItem('symbols_session_source', 'own')
        restoredOwnRefresh = true
      } catch (_) {
        /* locked storage — nothing to restore into */
      }
      return null
    }
    const bridgeHydration = Promise.resolve(
      legacyRefreshCopy
        ? reacquireBridgeSession({
            // The FIRST (cold) ask: staging answered at 4,202 / 4,460 ms, past the
            // helper's 4 s default (2026-10-11). Later re-asks keep the 4 s.
            timeoutMs: 8000,
            onNoSession: () => {
              peerSaidNoSession = true
            }
          }).then(
            (got) =>
              got || (peerSaidNoSession && !copyWasBridgeMarked ? restoreOwnRefresh() : null),
            () => null
          )
        : typeof reacquireBridgeSession === 'function'
          ? reacquireBridgeSession({ ifStale: true, timeoutMs: 8000 })
          : hydrateAuthFromIframeBridge()
    ).catch(() => null)
    state.crossAppHydration = bridgeHydration.then((v) => !!v)
    const opts = { debug: false }
    if (apiUrl) opts.apiUrl = apiUrl
    const sdk = new SDK(opts)
    // `workspaceProjectTokenProvider` supplies the bearer for
    // /workspace-project/* — it prefers the SDK Mongo token
    // (`workspaceTokenProvider` above).
    //
    // Workspace/org switching goes through `sdk.switchWorkspace(wsId)`,
    // which writes the active workspace to Mongo (the source of truth the
    // workspace-project userResolver reads). The old governance-Supabase
    // federation re-mint callback was removed with that project.
    //
    // `tracking.workspaceId` stamps `envelope.workspace_id` on every
    // /core/analyzed/ingest call. Reads the same `activeWorkspace`
    // localStorage key bootShell uses, so returning users get correct
    // cross-tenant attribution even during the auth/me race window.
    // Authenticated steady-state is resolved server-side from the auth
    // context regardless; this only covers the in-flight pre-attachUser
    // window + anonymous events.
    const initRaw = sdk
      .initialize({
        workspaceProjectTokenProvider: workspaceTokenProvider,
        // ACCESS-ONLY peer session: on expiry the SDK asks the bridge again
        // instead of signing out (TokenManager.setSessionReacquirer; an SDK
        // without it ignores this key and the renewal below covers it).
        // Undefined on an owner origin — see OWNER vs PEER above.
        reacquireSession: bridgeReacquirer,
        tracking: {
          // ANALYZING-DOUBLE-BOOT-1 (tickets/analytics.md): the SDK's
          // TrackingService is ENABLED BY DEFAULT and boots its OWN
          // createAnalyzing client (appName default 'workspace') — a SECOND
          // full analytics session per page load beside the shell's
          // bootAnalyzing client, with its own session id and its own
          // workspace stamp (this localStorage read vs the shell resolver —
          // the measured different-tenant pair). The shell's client is the
          // featured one (capture wiring, identify, session rotation), so
          // the SDK's stays OFF here. workspaceId kept so a deliberate
          // re-enable stamps correctly.
          enabled: false,
          workspaceId: readActiveWorkspaceId()
        }
      })
      .catch((err) => {
        console.warn('[sdk] initialize failed:', err?.message || err)
      })
    // Once init AND the cold-boot handoff settle: a borrowed session the
    // TokenManager did not load (it reads storage at construction, the
    // handoff lands later) is adopted as access-only, then the renewal for an
    // SDK without the reacquirer hook is armed (sessionBridgeRenewal.js).
    const settled = Promise.all([initRaw, bridgeHydration])
      .then(([, borrowed]) => {
        const tm = sdk._tokenManager
        if (restoredOwnRefresh) {
          // The copy was this origin's own login: let the TokenManager load it.
          if (tm && typeof tm.loadTokens === 'function') tm.loadTokens()
        } else if (
          tm &&
          borrowed &&
          typeof borrowed === 'object' &&
          borrowed.access_token &&
          !tm.hasRefreshToken?.() &&
          tm.getAccessToken?.() !== borrowed.access_token
        ) {
          if (typeof tm.adoptAccessToken === 'function') tm.adoptAccessToken(borrowed)
          else if (typeof tm.setTokens === 'function') {
            tm.setTokens({
              access_token: borrowed.access_token,
              expires_in: borrowed.expires_at
                ? Math.max(1, Math.floor((borrowed.expires_at - Date.now()) / 1000))
                : undefined,
              token_type: 'Bearer'
            })
          }
        }
        if (typeof installBridgeSessionRenewal === 'function') installBridgeSessionRenewal(sdk)
      })
      .catch(() => {})
    // While a copied refresh token is being judged, no SDK call may run (it
    // would sign out on the stripped session): init waits for the bridge.
    const initPromise = legacyRefreshCopy ? settled.then(() => initRaw) : initRaw
    state.initPromiseByKey.set(cacheKey, initPromise)
    state.sdkByKey.set(cacheKey, wrapWithInitAwait(sdk, initPromise))
  }
  return state.sdkByKey.get(cacheKey)
}

// Await the SDK initialization promise for the given opts without calling
// initialize() a second time (which would wipe auth state on the real SDK).
export async function awaitSDKInit(opts = {}) {
  // Same frank stub, same host, second construction site. `getSDKInline`
  // below carries its own byte-identical `new SDK(opts)`, so without this
  // line a served record throws here too — and this one is AWAITED, so it
  // rejects a caller that was only asking "is init done yet". A null client
  // never initializes, so a settled promise is the honest answer. See the
  // null-client note in `getSDK` above.
  if (typeof SDK !== 'function') return
  // FA204 / frank trap — self-contained inline copies of the shared-state
  // accessor and the FULL `getSDK` singleton accessor (a bare sibling-
  // export call is exactly as unresolvable post-frank.toJSON as a
  // module-local read — duplicating the whole thing once here is the
  // tradeoff that keeps every OTHER file in the app importing `getSDK`
  // cross-file instead of duplicating it). Kept byte-identical to the
  // export above.
  function sdkClientState() {
    return (globalThis.__sdkClientState = globalThis.__sdkClientState || {
      sdkByKey: new Map(),
      initPromiseByKey: new Map(),
      crossAppHydration: null
    })
  }

  function getSDKInline({ useLocal = false, useNext = false } = {}) {
    async function workspaceTokenProvider() {
      if (typeof window === 'undefined') return null
      try {
        return window.localStorage.getItem('symbols_access_token')
      } catch {
        return null
      }
    }
    function readActiveWorkspaceId() {
      if (typeof window === 'undefined') return null
      try {
        return getActiveWorkspacePointer() || null
      } catch {
        return null
      }
    }
    function resolveApiUrl({ useLocal, useNext } = {}) {
      if (useLocal) return channelApiUrl('local')
      if (useNext) return channelApiUrl('next')
      if (typeof window !== 'undefined') return workspaceApiBase()
      return undefined
    }
    const SYNC_PASS = new Set(['getAuthToken', 'isReady', 'getToken', 'logout'])
    function deferredSubscribeUserEvents(sdk, initPromise) {
      return (handlers) => {
        let realUnsub = null
        let cancelled = false
        initPromise
          .then(() => {
            if (cancelled) return
            const fn = sdk.subscribeUserEvents
            if (typeof fn !== 'function') return
            try {
              realUnsub = fn.call(sdk, handlers)
            } catch (err) {
              console.warn('[sdk] subscribeUserEvents threw:', err?.message || err)
            }
          })
          .catch(() => {})
        return () => {
          cancelled = true
          if (typeof realUnsub === 'function') {
            try {
              realUnsub()
            } catch (_) {}
          }
        }
      }
    }
    // THE FOUR SYNCHRONOUS READS BEFORE INIT. The boot exposes this proxy as
    // el.sdk before `initialize()` settles (shared bootPrepare, both builds),
    // and the SDK builds its services IN initialize() — so before it
    // `getAuthToken()` threw "Service 'auth' not found". The answer must not
    // depend on timing: until the init settles the token reads come from the
    // stored session (what the TokenManager loads on init), isReady() is
    // false, and logout() waits for the init it would otherwise race
    // (sdkClient.lateInit.test.js).
    function syncBeforeInit(target, prop, value, initPromise) {
      // Only BEFORE init settles (tracked from the wrap, see wrapWithInitAwait):
      // after it, an error is a real error and is rethrown — never hidden
      // behind the stored token (one-boot review D10).
      return (...args) => {
        try {
          return value.apply(target, args)
        } catch (err) {
          if (!initPromise || initPromise.__settled) throw err
          if (prop === 'isReady') return false
          if (prop === 'logout') return initPromise.then(() => value.apply(target, args))
          // exactly the key the SDK's TokenManager loads on init — the same
          // identity it will answer with, and never a token that is not there
          try {
            return globalThis.localStorage?.getItem?.('symbols_access_token') || null
          } catch (_) {
            return null
          }
        }
      }
    }

    function wrapWithInitAwait(sdk, initPromise) {
      // settle tracking starts with the wrap, so the first read after init
      // already sees it settled
      if (initPromise && !initPromise.__settleTracked) {
        initPromise.__settleTracked = true
        initPromise.__settled = false
        initPromise.then(
          () => (initPromise.__settled = true),
          () => (initPromise.__settled = true)
        )
      }
      return new Proxy(sdk, {
        get(target, prop) {
          if (prop === 'subscribeUserEvents') {
            return deferredSubscribeUserEvents(target, initPromise)
          }
          const value = target[prop]
          if (typeof value !== 'function') return value
          if (SYNC_PASS.has(prop)) return syncBeforeInit(target, prop, value, initPromise)
          return (...args) => initPromise.then(() => value.apply(target, args))
        }
      })
    }

    const apiUrl = resolveApiUrl({ useLocal, useNext })
    const cacheKey = apiUrl || 'default'
    const state = sdkClientState()
    // Fail closed, as getSDK above: no API plane → no SDK, nothing to await.
    if (typeof window !== 'undefined' && apiUrl === '') return null
    if (!state.sdkByKey.has(cacheKey)) {
      // HOTFIX 2026-10-08 (P0) — see getSDK: never read or write the
      // `smbls_session` cookie; delete the copy a previous build left.
      try {
        if (typeof clearAuthCookie === 'function') clearAuthCookie()
      } catch (_) {
        /* no document (SSR) — nothing to delete */
      }
      // OWNER vs PEER vs COPY — byte-identical to getSDK above (review M2 + D1).
      // OWNER vs PEER vs COPY (review M2 + D1, 2026-10-09; Nika: "No, avoid the
      // sign-out"). symbols_session_source: 'own' = a sign-in on THIS origin wrote
      // the refresh token (markOwnSession / the SDK); 'bridge' = adopted from the
      // /_session-bridge peer. An origin with an own refresh token owns the login:
      // no reacquirer — when the server refuses it, it signs out, never borrows.
      // A refresh token WITHOUT the 'own' mark:
      //   - on the shell (not a borrower): its own login from before the mark
      //     existed → mark it 'own';
      //   - on the canvas (the borrower): a COPY an older bridge build handed
      //     over. Take it out of storage BEFORE the SDK loads (so the SDK can
      //     never rotate the shell's token), ask the bridge, and adopt its token.
      //     If the bridge has no session, the token was this origin's own login:
      //     put it back, mark it 'own'. Never a sign-out either way.
      let ownsRefreshToken = false
      let legacyRefreshCopy = null
      let restoredOwnRefresh = false
      // D1 (live on canvas.dev, 2026-10-11): restore a copy as 'own' ONLY on
      // the peer's explicit ok:false, and never on a 'bridge'-marked origin.
      let copyWasBridgeMarked = false
      let peerSaidNoSession = false
      try {
        if (typeof window !== 'undefined') {
          const ls = window.localStorage
          const refresh = ls.getItem('symbols_refresh_token')
          if (refresh && ls.getItem('symbols_session_source') !== 'own') {
            if (
              typeof isSessionBridgeBorrower === 'function' &&
              isSessionBridgeBorrower() &&
              typeof reacquireBridgeSession === 'function'
            ) {
              legacyRefreshCopy = refresh
              copyWasBridgeMarked = ls.getItem('symbols_session_source') === 'bridge'
              ls.removeItem('symbols_refresh_token')
            } else {
              ls.setItem('symbols_session_source', 'own')
            }
          }
          ownsRefreshToken = !!ls.getItem('symbols_refresh_token')
        }
      } catch (_) {
        /* locked storage — treat as no own login */
      }
      // The SDK (>= 3.14.813) passes its re-ask options ({ timeoutMs, onNoSession }).
      const bridgeReacquirer =
        !ownsRefreshToken && typeof reacquireBridgeSession === 'function'
          ? (opts) => reacquireBridgeSession(opts)
          : undefined
      const restoreOwnRefresh = () => {
        try {
          window.localStorage.setItem('symbols_refresh_token', legacyRefreshCopy)
          window.localStorage.setItem('symbols_session_source', 'own')
          restoredOwnRefresh = true
        } catch (_) {
          /* locked storage — nothing to restore into */
        }
        return null
      }
      const bridgeHydration = Promise.resolve(
        legacyRefreshCopy
          ? reacquireBridgeSession({
              // The FIRST (cold) ask: staging answered at 4,202 / 4,460 ms, past the
              // helper's 4 s default (2026-10-11). Later re-asks keep the 4 s.
              timeoutMs: 8000,
              onNoSession: () => {
                peerSaidNoSession = true
              }
            }).then(
              (got) =>
                got || (peerSaidNoSession && !copyWasBridgeMarked ? restoreOwnRefresh() : null),
              () => null
            )
          : typeof reacquireBridgeSession === 'function'
            ? reacquireBridgeSession({ ifStale: true, timeoutMs: 8000 })
            : hydrateAuthFromIframeBridge()
      ).catch(() => null)
      state.crossAppHydration = bridgeHydration.then((v) => !!v)
      const opts = { debug: false }
      if (apiUrl) opts.apiUrl = apiUrl
      const sdk = new SDK(opts)
      const initRaw = sdk
        .initialize({
          workspaceProjectTokenProvider: workspaceTokenProvider,
          reacquireSession: bridgeReacquirer,
          tracking: {
            // ANALYZING-DOUBLE-BOOT-1 (tickets/analytics.md): the SDK's
            // TrackingService is ENABLED BY DEFAULT and boots its OWN
            // createAnalyzing client (appName default 'workspace') — a SECOND
            // full analytics session per page load beside the shell's
            // bootAnalyzing client, with its own session id and its own
            // workspace stamp (this localStorage read vs the shell resolver —
            // the measured different-tenant pair). The shell's client is the
            // featured one (capture wiring, identify, session rotation), so
            // the SDK's stays OFF here. workspaceId kept so a deliberate
            // re-enable stamps correctly.
            enabled: false,
            workspaceId: readActiveWorkspaceId()
          }
        })
        .catch((err) => {
          console.warn('[sdk] initialize failed:', err?.message || err)
        })
      const settled = Promise.all([initRaw, bridgeHydration])
        .then(([, borrowed]) => {
          const tm = sdk._tokenManager
          if (restoredOwnRefresh) {
            // The copy was this origin's own login: let the TokenManager load it.
            if (tm && typeof tm.loadTokens === 'function') tm.loadTokens()
          } else if (
            tm &&
            borrowed &&
            typeof borrowed === 'object' &&
            borrowed.access_token &&
            !tm.hasRefreshToken?.() &&
            tm.getAccessToken?.() !== borrowed.access_token
          ) {
            if (typeof tm.adoptAccessToken === 'function') tm.adoptAccessToken(borrowed)
            else if (typeof tm.setTokens === 'function') {
              tm.setTokens({
                access_token: borrowed.access_token,
                expires_in: borrowed.expires_at
                  ? Math.max(1, Math.floor((borrowed.expires_at - Date.now()) / 1000))
                  : undefined,
                token_type: 'Bearer'
              })
            }
          }
          if (typeof installBridgeSessionRenewal === 'function') installBridgeSessionRenewal(sdk)
        })
        .catch(() => {})
      // While a copied refresh token is being judged, no SDK call may run (it
      // would sign out on the stripped session): init waits for the bridge.
      const initPromise = legacyRefreshCopy ? settled.then(() => initRaw) : initRaw
      state.initPromiseByKey.set(cacheKey, initPromise)
      state.sdkByKey.set(cacheKey, wrapWithInitAwait(sdk, initPromise))
    }
    return state.sdkByKey.get(cacheKey)
  }

  function resolveApiUrl({ useLocal, useNext } = {}) {
    if (useLocal) return channelApiUrl('local')
    if (useNext) return channelApiUrl('next')
    if (typeof window !== 'undefined') return workspaceApiBase()
    return undefined
  }

  const cacheKey = resolveApiUrl(opts) || 'default'
  getSDKInline(opts)
  return sdkClientState().initPromiseByKey.get(cacheKey)
}

// Await the cross-app iframe handoff. Callers that gate on "is this user
// signed in" must await this first on a cold load, otherwise they can
// decide "signed out" while the peer origin's session is still being
// fetched. Safe to call before getSDK (resolves immediately if no
// hydration was ever started) and safe to call repeatedly.
export function awaitCrossAppHydration() {
  function sdkClientState() {
    return (globalThis.__sdkClientState = globalThis.__sdkClientState || {
      sdkByKey: new Map(),
      initPromiseByKey: new Map(),
      crossAppHydration: null
    })
  }
  return sdkClientState().crossAppHydration || Promise.resolve(false)
}
